/**
 * @fileoverview UI компонент для отображения статуса геолокации.
 * Показывает состояние получения координат с возможностью повтора и просмотра ошибок.
 * 
 * @since 1.0.0
 */

import React from 'react';
import { GeolocationStatus } from '../../utils/hooks/useGeolocation';
import styles from './styles.module.sass';

/**
 * Пропсы компонента GeolocationPanel.
 * 
 * @since 1.0.0
 */
interface GeolocationPanelProps {
    /** Текущий статус получения геолокации */
    status: GeolocationStatus;
    /** Текст ошибки для отображения пользователю */
    error?: string | null;
    /** Коллбэк для повторного запроса геолокации */
    onRetry: () => void;
    /** Коллбэк для отображения детальной информации об ошибке */
    onShowInfo: () => void;
}

/**
 * Иконка загрузки с анимацией спиннера.
 * 
 * @returns JSX элемент с анимированным спиннером
 * 
 * @since 1.0.0
 */
const LoadingIcon: React.FC = () => (
    <div className={styles.loadingIcon}>
        <div className={styles.spinner}></div>
    </div>
);

/**
 * Иконка успешного выполнения (галочка).
 * 
 * @returns SVG иконка с галочкой
 * 
 * @since 1.0.0
 */
const SuccessIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.successIcon}>
        <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="currentColor"/>
    </svg>
);

/**
 * Иконка ошибки (крестик).
 * 
 * @returns SVG иконка с крестиком
 * 
 * @since 1.0.0
 */
const ErrorIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.errorIcon}>
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
    </svg>
);

/**
 * Иконка информации (символ "i" в круге).
 * 
 * @returns SVG иконка с символом информации
 * 
 * @since 1.0.0
 */
const InfoIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.infoIcon}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="currentColor"/>
    </svg>
);

/**
 * Иконка повтора (стрелка обновления).
 * 
 * @returns SVG иконка с стрелкой обновления
 * 
 * @since 1.0.0
 */
const RetryIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.retryIcon}>
        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
    </svg>
);

/**
 * Панель отображения статуса получения геолокации.
 * 
 * Показывает текущее состояние процесса получения координат с соответствующими
 * иконками и предоставляет возможности для повтора и просмотра деталей ошибок.
 * 
 * **Отображаемые состояния:**
 * - **loading**: Спиннер + "Получение геолокации..."
 * - **success**: Галочка + "Геолокация получена"
 * - **error**: Крестик + "Ошибка геолокации" + кнопки действий
 * 
 * **Интерактивность:**
 * - При ошибке показывает кнопки "Информация" и "Повтор"
 * - Кнопка информации вызывает onShowInfo для показа деталей
 * - Кнопка повтора вызывает onRetry для нового запроса геолокации
 * 
 * @param props - Пропсы компонента
 * 
 * @returns JSX элемент с панелью статуса геолокации
 * 
 * @remarks
 * Компонент интегрирован с хуком useGeolocation и отображается
 * постоянно в верхней части интерфейса WebAppCam для информирования
 * пользователя о состоянии получения координат.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * const { status, error, retry } = useGeolocation();
 * 
 * <GeolocationPanel
 *   status={status}
 *   error={error}
 *   onRetry={retry}
 *   onShowInfo={() => telegramApi.showAlert(error)}
 * />
 * ```
 * 
 * @see {@link useGeolocation} для получения статуса геолокации
 */
export const GeolocationPanel: React.FC<GeolocationPanelProps> = ({
    status,
    error,
    onRetry,
    onShowInfo
}) => {
    const getStatusText = () => {
        switch (status) {
            case 'loading':
                return 'Получение геолокации...';
            case 'success':
                return 'Геолокация получена';
            case 'error':
                return 'Ошибка геолокации';
            default:
                return '';
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'loading':
                return <LoadingIcon />;
            case 'success':
                return <SuccessIcon />;
            case 'error':
                return <ErrorIcon />;
            default:
                return null;
        }
    };

    return (
        <div className={styles.panel}>
            <div className={styles.statusContainer}>
                <div className={styles.iconContainer}>
                    {getStatusIcon()}
                </div>
                <span className={styles.statusText}>
                    {getStatusText()}
                </span>
            </div>
            
            {status === 'error' && (
                <div className={styles.buttonsContainer}>
                    <button 
                        className={styles.actionButton}
                        onClick={onShowInfo}
                        title="Показать информацию об ошибке"
                    >
                        <InfoIcon />
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={onRetry}
                        title="Повторить запрос геолокации"
                    >
                        <RetryIcon />
                    </button>
                </div>
            )}
        </div>
    );
}; 